import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  Button,
  Card,
  CardTitle,
  CardContent,
  StatusBadge
} from '../../styles/PageStyles';
import { Network, Workflow, Bot, ClipboardList, Activity, FileText, Clock, User } from 'lucide-react';
import { Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
} from 'chart.js';
import PageHeaderWithInfo from '../../components/common/PageHeaderWithInfo';
import { getMetricRunById, getChildMetricRuns } from '../../services/ObservabilityService';

const LayoutContainer = styled.div`
  display: grid;
  grid-template-columns:
    minmax(260px, 3fr)
    minmax(0, 10fr);
  gap: 1.25rem;
  margin-top: 1rem;
  align-items: flex-start;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
`;

const FieldLabel = styled.div`
  color: var(--text-secondary);
`;

const FieldValue = styled.div`
  text-align: right;
  max-width: 60%;
  color: var(--text-primary);
  word-break: break-all;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.7rem;
`;

const HeaderMetricPill = styled.div`
  padding: 0.7rem 1rem;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 180px;
  justify-content: center;
`;

const HeaderMetricLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const HeaderMetricValue = styled.span`
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--text-primary);
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
`;

const TabButton = styled.button`
  padding: 0.5rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid ${(props) => (props.active ? 'var(--primary)' : 'transparent')};
  background: transparent;
  color: ${(props) => (props.active ? 'var(--primary)' : 'var(--text-secondary)')};
  font-weight: ${(props) => (props.active ? 600 : 400)};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const TabPanel = styled.div`
  padding: 0.75rem 0.25rem;
`;

const MessageCard = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  position: relative;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const TimelineRow = styled.div`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 0.75rem;
`;

const TimelineRail = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e5e7eb;
`;

const TimelineDot = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #2563eb;
  border: 2px solid #ffffff;
  margin-top: 4px;
  z-index: 1;
`;

const RoleBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 0.7rem;
  color: #374151;
`;

const MessageMeta = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  font-size: 0.8rem;
  color: #111827;
`;

const JsonBlock = styled.pre`
  background: #f3f4f6;
  color: #111827;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.75rem;
  max-height: 260px;
  overflow: auto;
`;

const MarkdownContainer = styled.div`
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--text-primary);
  max-height: 400px;
  overflow-y: auto;
  padding: 0.5rem 0.25rem;

  & h1 {
    font-size: 1.1rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  & h2 {
    font-size: 1rem;
    margin-top: 0.75rem;
    margin-bottom: 0.4rem;
  }
  & h3 {
    font-size: 0.95rem;
    margin-top: 0.5rem;
    margin-bottom: 0.3rem;
  }
  & p {
    margin-bottom: 0.5rem;
  }
  & ul, & ol {
    margin-left: 1.5rem;
    margin-bottom: 0.5rem;
  }
  & code {
    background: #f3f4f6;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-size: 0.8rem;
  }
  & pre {
    background: #f3f4f6;
    padding: 0.75rem;
    border-radius: 6px;
    overflow-x: auto;
    margin-bottom: 0.5rem;
  }
  & pre code {
    background: transparent;
    padding: 0;
  }
`;

const StepsHeader = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const StepItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.6rem;
  border-radius: 6px;
  border: 1px solid ${(props) => (props.selected ? '#2563eb' : 'transparent')};
  background: ${(props) => (props.selected ? '#eff6ff' : '#f9fafb')};
  cursor: pointer;
  font-size: 0.8rem;
  color: #111827;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const StepItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StepItemTitle = styled.div`
  font-weight: 500;
  color: #111827;
`;

const StepItemMeta = styled.div`
  font-size: 0.7rem;
  color: #4b5563;
`;

const StepItemSubtitle = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
`;

const StepChip = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  border: 1px solid #e5e7eb;
  background: #eff6ff;
  color: #1d4ed8;
`;

const ReasoningSummaryRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
`;

const ReasoningSummaryCard = styled.div`
  flex: 1;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
`;

const ReasoningSummaryLabel = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 4px;
`;

const ReasoningSummaryValue = styled.p`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${(p) => (p.$success ? '#1D9E75' : '#111827')};
`;

const ReasoningControlsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const ReasoningToggleButton = styled.button`
  font-family: inherit;
  font-size: 13px;
  color: #2563eb;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
`;

const ReasoningStepper = styled.div`
  position: relative;
`;

const ReasoningStepperLine = styled.div`
  content: '';
  position: absolute;
  left: 16px;
  top: 12px;
  bottom: 12px;
  width: 1px;
  background: #e5e7eb;
`;

const ReasoningStepRow = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 14px;
  margin-bottom: 16px;
`;

const ReasoningMarker = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => (p.$final ? '#1D9E75' : '#ffffff')};
  border: 1px solid ${(p) => (p.$final ? '#1D9E75' : '#C7CDD6')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.$final ? '#ffffff' : '#1B3A6B')};
  z-index: 1;
`;

const ReasoningCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
`;

const ReasoningHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReasoningTitle = styled.p`
  font-weight: 700;
  font-size: 15px;
  margin: 0;
  color: #111827;
`;

const ReasoningHint = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 6px 0 0 0;
`;

const ReasoningBody = styled.div`
  display: ${(p) => (p.$expanded ? 'block' : 'none')};
  font-size: 13px;
  line-height: 1.6;
  color: #4b5563;
  margin-top: 10px;
`;

const ReasoningBadge = styled.span`
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 6px;
  white-space: nowrap;
  background: ${(p) => (p.$variant === 'continue' ? '#E6F1FB' : p.$variant === 'validate' ? '#FAEEDA' : p.$variant === 'final_answer' ? '#E1F5EE' : '#f3f4f6')};
  color: ${(p) => (p.$variant === 'continue' ? '#1B3A6B' : p.$variant === 'validate' ? '#BA7517' : p.$variant === 'final_answer' ? '#1D9E75' : '#374151')};
`;

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const ConfidenceLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const ConfidenceTrack = styled.div`
  flex: 1;
  max-width: 140px;
  height: 4px;
  background: #EEF0F3;
  border-radius: 2px;
`;

const ConfidenceFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${(p) => (p.$low ? '#E24B4A' : '#1D9E75')};
  width: ${(p) => p.$pct || 0}%;
`;

const ConfidenceValue = styled.span`
  font-size: 12px;
  color: #4b5563;
`;

// --- Execution hierarchy (agent / planner runs) ---

const TraceTreeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TraceNodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const TraceNodeHeaderButton = styled.button`
  width: 100%;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$selected ? '#bfdbfe' : 'transparent')};
  background: ${(props) => (props.$selected ? '#eff6ff' : '#f9fafb')};
  padding: 0.45rem 0.55rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 0.78rem;
  color: #111827;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${(props) => (props.$selected ? '#e0edff' : '#f3f4f6')};
  }
`;

const TraceNodeLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex: 1;
  min-width: 0;
`;

const TraceNodeIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const TraceNodeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const TraceNodeName = styled.div`
  font-weight: 500;
  color: #111827;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TraceNodeMeta = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const TraceNodeStatusBadge = styled.span`
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
`;

const TraceChildren = styled.div`
  margin-left: 14px;
  padding-left: 12px;
  border-left: 1px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const PreviewLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const PreviewTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
`;

const PreviewSubtitle = styled.div`
  font-size: 0.8rem;
  color: #4b5563;
`;

const PreviewChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
`;

const PreviewChip = styled.span`
  padding: 3px 8px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  font-size: 0.7rem;
  color: #3730a3;
`;

const PerformanceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const PerformanceCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  // padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const GaugesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  // padding: 1rem 0;
`;

const GaugeSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid #ccc;
  padding: 1.5rem;
  padding-bottom: 2.5rem;
  border-radius: 16px;
  position: relative;
`;

const PerformanceCardTitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const PerformanceCardValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const PerformanceCardLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-top: 10px;
  font-weight: bold;
`;

const PerformanceCardStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 1rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const PerformanceCardStat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const formatDurationMs = (ms) => {
  if (ms == null || Number.isNaN(ms)) return 'n/a';
  const sec = ms / 1000;
  const min = sec / 60;
  const hr = min / 60;
  const day = hr / 24;
  if (day >= 1) {
    const days = Math.floor(day);
    const remainingHours = Math.floor(hr - days * 24);
    return `${days}d ${remainingHours}h`;
  }
  if (hr >= 1) {
    const hoursRounded = Math.round(hr * 10) / 10;
    return `${hoursRounded}h`;
  }
  if (min >= 1) {
    const minutesRounded = Math.round(min * 10) / 10;
    return `${minutesRounded}min`;
  }
  if (sec >= 1) {
    const secondsRounded = Math.round(sec * 10) / 10;
    return `${secondsRounded}s`;
  }
  const msRounded = Math.round(ms);
  return `${msRounded}ms`;
};

const formatTimestamp = (value) => {
  if (value === null || value === undefined) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // DD-MM-YYYY HH:mm:ss
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const formatTimeOnly = (value) => {
  if (value === null || value === undefined) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${hours}:${minutes}:${seconds}`;
};

const safeNumber = (value) => (typeof value === 'number' ? value : value == null ? 'n/a' : value);

ChartJS.register(ArcElement, ChartTooltip, ChartLegend, CategoryScale, LinearScale, PointElement, LineElement);

const buildTokenDonutData = (inputTokens, outputTokens) => {
  const inVal = typeof inputTokens === 'number' ? inputTokens : 0;
  const outVal = typeof outputTokens === 'number' ? outputTokens : 0;
  if (!inVal && !outVal) {
    return null;
  }
  return {
    labels: ['Input', 'Output'],
    datasets: [
      {
        data: [inVal, outVal],
        backgroundColor: ['#4f46e5', '#f97316'],
        borderWidth: 0
      }
    ]
  };
};

const buildMemberTokenPieData = (memberResponses) => {
  if (!Array.isArray(memberResponses) || memberResponses.length === 0) return null;
  const withTokens = memberResponses.filter(
    (mr) => mr && mr.metrics && typeof mr.metrics.total_tokens === 'number' && mr.metrics.total_tokens > 0
  );
  if (!withTokens.length) return null;

  const labels = withTokens.map((mr, idx) => mr.agent_name || mr.agent_id || `Member ${idx + 1}`);
  const data = withTokens.map((mr) => mr.metrics.total_tokens);
  const baseColors = ['#4f46e5', '#f97316', '#10b981', '#0ea5e9', '#ec4899', '#22c55e', '#6366f1', '#facc15'];
  const backgroundColor = withTokens.map((_, idx) => baseColors[idx % baseColors.length]);

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor,
        borderWidth: 0
      }
    ]
  };
};

const tokenDonutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(255,255,255,0.96)',
      titleColor: '#111827',
      bodyColor: '#4b5563',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: 8
    }
  },
  cutout: '70%'
};

const buildSparklineData = (points) => ({
  labels: points.map((_, idx) => String(idx + 1)),
  datasets: [
    {
      data: points,
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34,197,94,0.15)',
      tension: 0.4,
      borderWidth: 1.5,
      pointRadius: 0
    }
  ]
});

const sparklineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    y: { display: false }
  }
};

const buildScalarSparklineData = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const points = Array(8).fill(value);
  return buildSparklineData(points);
};

// --- Helpers for execution hierarchy ---

const formatShortDurationSeconds = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return null;
  if (seconds < 1) {
    const ms = Math.round(seconds * 1000);
    return `${ms}ms`;
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = seconds / 60;
  return `${minutes.toFixed(1)}min`;
};

const getRoleBadgeStyle = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'system') {
    return {
      backgroundColor: 'rgba(129, 140, 248, 0.18)',
      color: '#4c1d95'
    };
  }
  if (r === 'assistant') {
    return {
      backgroundColor: 'rgba(34, 197, 94, 0.14)',
      color: '#166534'
    };
  }
  if (r === 'user') {
    return {
      backgroundColor: 'rgba(59, 130, 246, 0.14)',
      color: '#1d4ed8'
    };
  }
  return {
    backgroundColor: '#f3f4f6',
    color: '#374151'
  };
};

const getStepTypeLabel = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'pipeline') return 'Pipeline';
  if (t === 'agent') return 'Agent';
  if (t === 'planner') return 'Planner';
  if (t === 'code') return 'Code';
  if (t === 'entity_operation') return 'Entity';
  if (t === 'manual_task' || t === 'manual') return 'Manual Task';
  if (t === 'system') return 'System';
  return 'Step';
};

const getStepChipStyle = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'pipeline') {
    return { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' };
  }
  if (t === 'agent') {
    return { backgroundColor: 'rgba(34,197,94,0.14)', borderColor: '#bbf7d0', color: '#166534' };
  }
  if (t === 'planner') {
    return { backgroundColor: 'rgba(129,140,248,0.18)', borderColor: '#c7d2fe', color: '#4c1d95' };
  }
  if (t === 'manual_task' || t === 'manual') {
    return { backgroundColor: '#fffbeb', borderColor: '#fed7aa', color: '#92400e' };
  }
  if (t === 'entity_operation') {
    return { backgroundColor: '#ecfeff', borderColor: '#a5f3fc', color: '#155e75' };
  }
  if (t === 'system') {
    return { backgroundColor: '#f3f4f6', borderColor: '#e5e7eb', color: '#374151' };
  }
  return { backgroundColor: '#f3f4f6', borderColor: '#e5e7eb', color: '#374151' };
};

const getTraceStatusVariant = (status) => {
  if (!status) return 'default';
  const s = String(status).toLowerCase();
  if (['completed', 'success', 'ok'].includes(s)) return 'success';
  if (['failed', 'error'].includes(s)) return 'error';
  if (['running', 'in_progress', 'pending'].includes(s)) return 'warning';
  return 'default';
};

const getTraceStatusColors = (variant) => {
  if (variant === 'success') {
    return { background: 'rgba(34,197,94,0.12)', color: '#16a34a' };
  }
  if (variant === 'error') {
    return { background: 'rgba(239,68,68,0.12)', color: '#b91c1c' };
  }
  if (variant === 'warning') {
    return { background: 'rgba(245,158,11,0.12)', color: '#92400e' };
  }
  return { background: '#f3f4f6', color: '#374151' };
};

const getTraceNodeIconStyle = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'planner') {
    return { backgroundColor: 'rgba(129,140,248,0.18)', color: '#4c1d95', label: '🧠' };
  }
  if (t === 'agent' || t === 'member') {
    return { backgroundColor: 'rgba(59,130,246,0.14)', color: '#1d4ed8', label: '🤖' };
  }
  if (t === 'node') {
    return { backgroundColor: 'rgba(13,148,136,0.14)', color: '#0d9488', label: '🧩' };
  }
  if (t === 'tool') {
    return { backgroundColor: 'rgba(34,197,94,0.14)', color: '#166534', label: '🛠' };
  }
  if (t === 'llm') {
    return { backgroundColor: 'rgba(249,115,22,0.14)', color: '#c2410c', label: '✨' };
  }
  if (t === 'db') {
    return { backgroundColor: 'rgba(236,72,153,0.14)', color: '#9d174d', label: '🗄' };
  }
  return { backgroundColor: '#e5e7eb', color: '#374151', label: '•' };
};

const formatToolLabel = (name) => {
  const raw = name || 'Tool call';
  const maxLen = 32;
  return raw.length > maxLen ? `${raw.slice(0, maxLen - 1)}…` : raw;
};

const buildPipelineStepTokenPieData = (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  const childSteps = steps.filter((s) => s && s.id !== 'pipeline-root');
  const withTokens = childSteps.filter((s) => typeof s.totalTokens === 'number' && s.totalTokens > 0);
  if (!withTokens.length) return null;

  const labels = withTokens.map((s) => s.label || 'Step');
  const data = withTokens.map((s) => s.totalTokens);
  const baseColors = ['#4f46e5', '#f97316', '#10b981', '#0ea5e9', '#ec4899', '#22c55e', '#6366f1', '#facc15'];
  const backgroundColor = withTokens.map((_, idx) => baseColors[idx % baseColors.length]);

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor,
        borderWidth: 0
      }
    ]
  };
};

const preprocessMarkdownContent = (content) => {
  if (!content || typeof content !== 'string') return content;

  // Detect if content contains Python dictionary patterns like {'key': 'value'}
  const pythonDictPattern = /\{[^{}]*?:\s*['"]/;

  if (pythonDictPattern.test(content)) {
    // Try to extract and format Python dictionaries/JSON objects
    const lines = content.split('\n');
    const processedLines = [];
    let inDict = false;
    let dictBuffer = '';
    let beforeDict = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check if line contains a dictionary start
      if (line.includes('{') && (line.includes("'") || line.includes('"')) && line.includes(':')) {
        inDict = true;
        dictBuffer = line;

        // Check if it's a single-line dict (unlikely for large dicts)
        const openBraces = (line.match(/\{/g) || []).length;
        const closeBraces = (line.match(/\}/g) || []).length;

        if (openBraces === closeBraces && line.includes('}')) {
          inDict = false;
          try {
            // Try to parse and format as structured markdown
            const jsonStr = dictBuffer
              .replace(/'/g, '"')
              .replace(/(\w+):/g, '"$1":');
            const parsed = JSON.parse(jsonStr);

            // Check if values contain markdown (## headings, newlines, etc.)
            const hasMarkdownValues = Object.values(parsed).some(val =>
              typeof val === 'string' && (val.includes('##') || val.includes('\n'))
            );

            if (hasMarkdownValues) {
              // Render as expandable sections with markdown
              Object.entries(parsed).forEach(([key, value]) => {
                processedLines.push(`### ${key}`);
                processedLines.push('');
                processedLines.push(value);
                processedLines.push('');
                processedLines.push('---');
                processedLines.push('');
              });
            } else {
              // Regular JSON code block
              processedLines.push('```json');
              processedLines.push(JSON.stringify(parsed, null, 2));
              processedLines.push('```');
            }
          } catch (e) {
            // If parsing fails, wrap in code block as-is
            processedLines.push('```');
            processedLines.push(dictBuffer);
            processedLines.push('```');
          }
          dictBuffer = '';
        }
      } else if (inDict) {
        dictBuffer += '\n' + line;

        // Count braces to detect end of dict
        const openBraces = (dictBuffer.match(/\{/g) || []).length;
        const closeBraces = (dictBuffer.match(/\}/g) || []).length;

        if (openBraces === closeBraces && openBraces > 0) {
          inDict = false;
          try {
            // Try to parse and format as structured markdown
            const jsonStr = dictBuffer
              .replace(/'/g, '"')
              .replace(/(\w+):/g, '"$1":');
            const parsed = JSON.parse(jsonStr);

            // Check if values contain markdown (## headings, newlines, etc.)
            const hasMarkdownValues = Object.values(parsed).some(val =>
              typeof val === 'string' && (val.includes('##') || val.includes('\n'))
            );

            if (hasMarkdownValues) {
              // Render as expandable sections with markdown
              Object.entries(parsed).forEach(([key, value]) => {
                processedLines.push(`### ${key}`);
                processedLines.push('');
                if (typeof value === 'string') {
                  processedLines.push(value);
                } else {
                  processedLines.push('```json');
                  processedLines.push(JSON.stringify(value, null, 2));
                  processedLines.push('```');
                }
                processedLines.push('');
                processedLines.push('---');
                processedLines.push('');
              });
            } else {
              // Regular JSON code block
              processedLines.push('```json');
              processedLines.push(JSON.stringify(parsed, null, 2));
              processedLines.push('```');
            }
          } catch (e) {
            // If parsing fails, wrap in code block as-is
            processedLines.push('```');
            processedLines.push(dictBuffer);
            processedLines.push('```');
          }
          dictBuffer = '';
        }
      } else {
        processedLines.push(line);
      }
    }

    return processedLines.join('\n');
  }

  return content;
};

const ObservabilityRunDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('request-response');
  const [pipelineTab, setPipelineTab] = useState('summary');
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [childRuns, setChildRuns] = useState([]);
  const [childRunsLoading, setChildRunsLoading] = useState(false);
  const [childRunsError, setChildRunsError] = useState(null);
  const [selectedTraceNodeId, setSelectedTraceNodeId] = useState(null);
  const [nestedChildrenMap, setNestedChildrenMap] = useState({}); // Map of runId -> array of children

  useEffect(() => {
    const fetchRun = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMetricRunById(id);
        setRun(data);
      } catch (e) {
        console.error('Failed to fetch metric run by id', e);
        setError('Failed to load metric run details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRun();
    }
  }, [id]);

  const metricType = run?.metric_type;
  const runData = run?.run_data || {};
  const messages = Array.isArray(runData.messages) ? runData.messages : [];
  const metrics = runData.metrics || {};

  const isPipelineRun = metricType === 'pipeline' || metricType === 'planner' || metricType === 'agent';
  const isPlannerRun = metricType === 'planner';
  const isAgentRun = metricType === 'agent';

  const pipelineName = isPipelineRun
    ? run?.metric_type_name ||
    runData.pipeline_name ||
    run?.pipeline_name ||
    run?.metric_type_code ||
    run?.metric_type ||
    id
    : null;

  const pipelineBusinessValue = isPipelineRun
    ? runData.business_value || run?.business_value || null
    : null;

  const pipelineCode = isPipelineRun
    ? run?.metric_type_code || runData.pipeline_code || run?.pipeline_code || run?.metric_type || 'n/a'
    : null;

  const pipelineCaseId = isPipelineRun
    ? runData.case_id ||
    runData.claim_id ||
    run?.case_id ||
    (runData.context && (runData.context.case_id || runData.context.claim_id)) ||
    null
    : null;

  let pipelineMemberLabel = null;
  if (isPipelineRun) {
    const member =
      (runData && typeof runData.member === 'object' && runData.member) ||
      (runData && typeof runData.member_info === 'object' && runData.member_info) ||
      null;
    const memberName = (member && (member.name || member.full_name)) || runData.member_name;
    const memberId = (member && (member.member_id || member.id)) || runData.member_id;
    if (memberName && memberId) {
      pipelineMemberLabel = `${memberName} (${memberId})`;
    } else {
      pipelineMemberLabel = memberName || memberId || null;
    }
  }

  const pipelineSteps = useMemo(() => {
    if (!isPipelineRun) return [];
    const steps = [];

    // Root pipeline step
    let durationMs = null;
    if (typeof run?.duration_ms === 'number') {
      durationMs = run.duration_ms;
    } else if (typeof metrics.duration === 'number') {
      durationMs = metrics.duration * 1000;
    }

    steps.push({
      id: 'pipeline-root',
      label: pipelineName || 'Pipeline',
      type: 'pipeline',
      status: run?.status || 'n/a',
      durationMs,
      startedAt: run?.started_at,
      finishedAt: run?.finished_at,
      tokensIn: metrics.input_tokens ?? run?.input_tokens,
      tokensOut: metrics.output_tokens ?? run?.output_tokens,
      totalTokens: metrics.total_tokens ?? run?.total_tokens,
      description:
        (runData.content && typeof runData.content === 'object' && runData.content.summary) ||
        (typeof runData.summary === 'string' ? runData.summary : '') ||
        null
    });

    // Child metric runs (agents/planners/etc.)
    const childArray = Array.isArray(childRuns)
      ? childRuns
      : childRuns && Array.isArray(childRuns.items)
        ? childRuns.items
        : [];

    childArray.forEach((child, index) => {
      if (!child) return;
      const childMetricType = child.metric_type || child.metricType;
      const type = childMetricType || 'agent';
      const childRunData = child.run_data || {};
      const childMetrics = childRunData.metrics || {};
      const stepMetrics = childMetrics && typeof childMetrics === 'object' ? childMetrics : {};
      const id = child.run_id || child.id || `child-${index}`;
      const label =
        child.metric_type_name ||
        child.agent_name ||
        child.planner_name ||
        childRunData.model ||
        `Step ${index + 1}`;
      const status = child.status || 'n/a';
      let childDurationMs = null;
      if (typeof child.duration_ms === 'number') {
        childDurationMs = child.duration_ms;
      } else if (typeof stepMetrics.duration === 'number') {
        childDurationMs = stepMetrics.duration * 1000;
      }

      steps.push({
        id,
        label,
        type,
        status,
        durationMs: childDurationMs,
        startedAt: child.started_at,
        finishedAt: child.finished_at,
        tokensIn: stepMetrics.input_tokens ?? child.input_tokens,
        tokensOut: stepMetrics.output_tokens ?? child.output_tokens,
        totalTokens: stepMetrics.total_tokens ?? child.total_tokens,
        description:
          (childRunData.content && typeof childRunData.content === 'object' && childRunData.content.summary) ||
          (typeof childRunData.summary === 'string' ? childRunData.summary : '') ||
          ''
      });
    });

    return steps;
  }, [isPipelineRun, runData, run, metrics, pipelineName, childRuns]);

  // --- Build execution hierarchy (pipeline runs and agent / planner runs) ---

  const traceTree = useMemo(() => {
    if (!run) return null;

    const flatMap = {};
    const register = (node) => {
      flatMap[node.id] = node;
    };

    // Pipeline runs: pipeline root -> child runs -> tools
    if (isPipelineRun) {
      const rootDurationSeconds =
        typeof metrics.duration === 'number'
          ? metrics.duration
          : typeof run.duration_ms === 'number'
            ? run.duration_ms / 1000
            : null;

      const rootTokens =
        metrics?.total_tokens ??
        run?.total_tokens ??
        null;

      const rootNode = {
        id: 'pipeline-root',
        type: 'pipeline',
        label:
          pipelineName ||
          run.metric_type_name ||
          run.metric_type ||
          'Pipeline run',
        status: run.status,
        durationSeconds: rootDurationSeconds,
        totalTokens: rootTokens,
        meta: {
          code: pipelineCode,
          user: run.user_id,
        },
        children: [],
        source: { kind: 'pipeline-run', run },
      };

      register(rootNode);

      const childArray = Array.isArray(childRuns)
        ? childRuns
        : childRuns && Array.isArray(childRuns.items)
          ? childRuns.items
          : [];

      // Recursive function to build child nodes
      const buildChildNode = (child, index) => {
        if (!child) return null;

        const childId = child.run_id || child.id || `child-${index}`;
        const childMetricType = child.metric_type || child.metricType;
        const childType =
          childMetricType === 'planner'
            ? 'planner'
            : childMetricType === 'agent'
              ? 'agent'
              : childMetricType === 'node'
                ? 'node'
                : 'agent';
        const childRunData =
          child.run_data && typeof child.run_data === 'object' ? child.run_data : {};
        const childMetrics = childRunData.metrics || {};
        const childDurationSeconds =
          typeof childMetrics.duration === 'number'
            ? childMetrics.duration
            : typeof child.duration_ms === 'number'
              ? child.duration_ms / 1000
              : null;
        const childTokens =
          childMetrics.total_tokens != null
            ? childMetrics.total_tokens
            : child.total_tokens ?? null;

        const childNode = {
          id: childId,
          type: childType,
          label:
            child.metric_type_name ||
            child.agent_name ||
            child.planner_name ||
            childRunData.model ||
            `Step ${index + 1}`,
          status: child.status,
          durationSeconds: childDurationSeconds,
          totalTokens: childTokens,
          meta: {
            model: childRunData.model,
          },
          children: [],
          source: { kind: 'child-run', run: child },
        };

        register(childNode);
        return childNode;
      };

      // Recursive function to add nested children
      const addNestedChildren = (parentNode, parentRunId) => {
        const nestedChildren = nestedChildrenMap[parentRunId] || [];
        nestedChildren.forEach((nestedChild, nestedIndex) => {
          const nestedChildNode = buildChildNode(nestedChild, nestedIndex);
          if (!nestedChildNode) return;
          
          parentNode.children.push(nestedChildNode);
          
          const nestedChildId = nestedChild.run_id || nestedChild.id;
          const nestedChildRunData = nestedChild.run_data && typeof nestedChild.run_data === 'object' ? nestedChild.run_data : {};
          
          // Add tools for nested child
          const nestedTools = Array.isArray(nestedChildRunData.tools) ? nestedChildRunData.tools : [];
          nestedTools.forEach((tool, toolIdx) => {
            const toolId = tool.tool_call_id || `${nestedChildId}-tool-${toolIdx}`;
            const durationSecondsTool = tool.metrics?.duration ?? null;
            const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

            const toolNode = {
              id: toolId,
              type: 'tool',
              label: formatToolLabel(tool.tool_name),
              status: statusVariant,
              durationSeconds: durationSecondsTool,
              totalTokens: null,
              meta: {
                resultPreview: typeof tool.result === 'string' ? tool.result.slice(0, 80) : '',
              },
              children: [],
              source: { kind: 'tool', tool, parentRun: nestedChild },
            };

            register(toolNode);
            nestedChildNode.children.push(toolNode);
          });
          
          // Recursively add children of this nested child
          if (nestedChildId) {
            addNestedChildren(nestedChildNode, nestedChildId);
          }
        });
      };

      childArray.forEach((child, index) => {
        const childNode = buildChildNode(child, index);
        if (!childNode) return;

        rootNode.children.push(childNode);
        const childId = child.run_id || child.id;
        const childRunData = child.run_data && typeof child.run_data === 'object' ? child.run_data : {};

        const tools = Array.isArray(childRunData.tools) ? childRunData.tools : [];
        tools.forEach((tool, toolIdx) => {
          const toolId = tool.tool_call_id || `${childId}-tool-${toolIdx}`;
          const durationSecondsTool = tool.metrics?.duration ?? null;
          const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

          const toolNode = {
            id: toolId,
            type: 'tool',
            label: formatToolLabel(tool.tool_name),
            status: statusVariant,
            durationSeconds: durationSecondsTool,
            totalTokens: null,
            meta: {
              resultPreview:
                typeof tool.result === 'string'
                  ? tool.result.slice(0, 80)
                  : '',
            },
            children: [],
            source: { kind: 'tool', tool, parentRun: child },
          };

          register(toolNode);
          childNode.children.push(toolNode);
        });

        const memberResponses = Array.isArray(childRunData.member_responses)
          ? childRunData.member_responses
          : [];

        memberResponses.forEach((mr, mrIdx) => {
          const memberId =
            mr.agent_id || mr.agent_name || mr.run_id || `${childId}-member-${mrIdx}`;
          const memberDurationSeconds = mr.metrics?.duration ?? null;
          const memberTokens = mr.metrics?.total_tokens ?? null;

          const memberNode = {
            id: memberId,
            type: 'member',
            label: mr.agent_name || mr.agent_id || 'Member run',
            status: mr.status,
            durationSeconds: memberDurationSeconds,
            totalTokens: memberTokens,
            meta: {
              model: mr.model,
            },
            children: [],
            source: { kind: 'member', member: mr, parentRun: child },
          };

          register(memberNode);
          childNode.children.push(memberNode);

          const memberTools = Array.isArray(mr.tools) ? mr.tools : [];
          memberTools.forEach((tool, toolIdx) => {
            const toolId = tool.tool_call_id || `${memberId}-tool-${toolIdx}`;
            const durationSecondsTool = tool.metrics?.duration ?? null;
            const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

            const toolNode = {
              id: toolId,
              type: 'tool',
              label: formatToolLabel(tool.tool_name),
              status: statusVariant,
              durationSeconds: durationSecondsTool,
              totalTokens: null,
              meta: {
                resultPreview:
                  typeof tool.result === 'string'
                    ? tool.result.slice(0, 80)
                    : '',
              },
              children: [],
              source: { kind: 'tool', tool, parentMember: mr, parentRun: child },
            };

            register(toolNode);
            memberNode.children.push(toolNode);
          });
        });
        
        // Add nested children recursively
        if (childId) {
          addNestedChildren(childNode, childId);
        }
      });

      const rootMemberResponses = Array.isArray(runData.member_responses)
        ? runData.member_responses
        : [];

      rootMemberResponses.forEach((mr, idx) => {
        const memberId =
          mr.agent_id || mr.agent_name || mr.run_id || `root-member-${idx}`;
        const memberDurationSeconds = mr.metrics?.duration ?? null;
        const memberTokens = mr.metrics?.total_tokens ?? null;

        const memberNode = {
          id: memberId,
          type: 'member',
          label: mr.agent_name || mr.agent_id || 'Member run',
          status: mr.status,
          durationSeconds: memberDurationSeconds,
          totalTokens: memberTokens,
          meta: {
            model: mr.model,
          },
          children: [],
          source: { kind: 'member', member: mr, parentRun: run },
        };

        register(memberNode);
        rootNode.children.push(memberNode);

        const memberTools = Array.isArray(mr.tools) ? mr.tools : [];
        memberTools.forEach((tool, toolIdx) => {
          const toolId = tool.tool_call_id || `${memberId}-tool-${toolIdx}`;
          const durationSecondsTool = tool.metrics?.duration ?? null;
          const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

          const toolNode = {
            id: toolId,
            type: 'tool',
            label: tool.tool_name || 'Tool call',
            status: statusVariant,
            durationSeconds: durationSecondsTool,
            totalTokens: null,
            meta: {
              resultPreview:
                typeof tool.result === 'string'
                  ? tool.result.slice(0, 80)
                  : '',
            },
            children: [],
            source: { kind: 'tool', tool, parentMember: mr, parentRun: run },
          };

          register(toolNode);
          memberNode.children.push(toolNode);
        });
      });

      // Tools directly on the pipeline run itself
      const topTools = Array.isArray(runData.tools) ? runData.tools : [];
      topTools.forEach((tool, idx) => {
        const toolId = tool.tool_call_id || `root-tool-${idx}`;
        const durationSecondsTool = tool.metrics?.duration ?? null;
        const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

        const toolNode = {
          id: toolId,
          type: 'tool',
          label: formatToolLabel(tool.tool_name),
          status: statusVariant,
          durationSeconds: durationSecondsTool,
          totalTokens: null,
          meta: {
            resultPreview:
              typeof tool.result === 'string'
                ? tool.result.slice(0, 80)
                : '',
          },
          children: [],
          source: { kind: 'tool', tool, parentRun: run },
        };

        register(toolNode);
        rootNode.children.push(toolNode);
      });

      return { rootNode, flatMap };
    }

    // Agent / planner runs: existing hierarchy (run -> members -> tools)

    const rootType = metricType === 'planner' ? 'planner' : 'agent';

    const rootDurationSeconds =
      typeof metrics.duration === 'number'
        ? metrics.duration
        : typeof run.duration_ms === 'number'
          ? run.duration_ms / 1000
          : null;

    const rootTokens =
      metrics?.details?.model?.[0]?.total_tokens ??
      metrics.total_tokens ??
      run.total_tokens ??
      null;

    const rootNode = {
      id: run.run_id || run.id || 'root',
      type: rootType,
      label: run.metric_type_name || run.agent_name || run.metric_type || 'Run',
      status: run.status,
      durationSeconds: rootDurationSeconds,
      totalTokens: rootTokens,
      meta: {
        model: runData.model,
        user: run.user_id,
      },
      children: [],
      source: { kind: 'run', run },
    };

    register(rootNode);

    const memberResponses = Array.isArray(runData.member_responses)
      ? runData.member_responses
      : [];

    memberResponses.forEach((mr, idx) => {
      const memberId =
        mr.agent_id || mr.agent_name || mr.run_id || `member-${idx}`;
      const memberDurationSeconds =
        mr.metrics?.duration ?? null;
      const memberTokens =
        mr.metrics?.total_tokens ?? null;

      const memberNode = {
        id: memberId,
        type: 'member',
        label: mr.agent_name || mr.agent_id || 'Member run',
        status: mr.status,
        durationSeconds: memberDurationSeconds,
        totalTokens: memberTokens,
        meta: {
          model: mr.model,
        },
        children: [],
        source: { kind: 'member', member: mr },
      };

      register(memberNode);
      rootNode.children.push(memberNode);

      const tools = Array.isArray(mr.tools) ? mr.tools : [];
      tools.forEach((tool, toolIdx) => {
        const toolId = tool.tool_call_id || `${memberId}-tool-${toolIdx}`;
        const durationSecondsTool = tool.metrics?.duration ?? null;
        const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

        const toolNode = {
          id: toolId,
          type: 'tool',
          label: tool.tool_name || 'Tool call',
          status: statusVariant,
          durationSeconds: durationSecondsTool,
          totalTokens: null,
          meta: {
            resultPreview:
              typeof tool.result === 'string'
                ? tool.result.slice(0, 80)
                : '',
          },
          children: [],
          source: { kind: 'tool', tool, parentMember: mr, parentRun: run },
        };

        register(toolNode);
        memberNode.children.push(toolNode);
      });
    });

    // Also attach any top-level tools on the run itself
    const topTools = Array.isArray(runData.tools) ? runData.tools : [];
    topTools.forEach((tool, idx) => {
      const toolId = tool.tool_call_id || `root-tool-${idx}`;
      const durationSecondsTool = tool.metrics?.duration ?? null;
      const statusVariant = tool.tool_call_error ? 'ERROR' : 'SUCCESS';

      const toolNode = {
        id: toolId,
        type: 'tool',
        label: formatToolLabel(tool.tool_name),
        status: statusVariant,
        durationSeconds: durationSecondsTool,
        totalTokens: null,
        meta: {
          resultPreview:
            typeof tool.result === 'string'
              ? tool.result.slice(0, 80)
              : '',
        },
        children: [],
        source: { kind: 'tool', tool, parentRun: run },
      };

      register(toolNode);
      rootNode.children.push(toolNode);
    });

    return { rootNode, flatMap };
  }, [run, runData, metrics, metricType, isPipelineRun, childRuns, pipelineName, pipelineCode, nestedChildrenMap]);

  // For pipeline runs, we rely on the execution hierarchy selection instead of selectedStep
  useEffect(() => {
    if (!isPipelineRun) return;
    // keep selectedStepId for backwards compatibility in preview chips, but default to pipeline-root
    if (!selectedStepId && pipelineSteps.length) {
      setSelectedStepId('pipeline-root');
    }
  }, [isPipelineRun, pipelineSteps.length, selectedStepId]);

  const selectedStep =
    isPipelineRun && pipelineSteps.length
      ? pipelineSteps.find((s) => s.id === selectedStepId) || pipelineSteps[0]
      : null;

  const totalTokens = metrics.total_tokens != null ? metrics.total_tokens : run?.total_tokens;

  const pipelineInputTokens = metrics.input_tokens ?? run?.input_tokens;
  const pipelineOutputTokens = metrics.output_tokens ?? run?.output_tokens;

  const pipelineStepTokenPieData = useMemo(
    () => (isPipelineRun ? buildPipelineStepTokenPieData(pipelineSteps) : null),
    [isPipelineRun, pipelineSteps]
  );

  const pipelineTokenDonutData = useMemo(
    () => (isPipelineRun ? buildTokenDonutData(pipelineInputTokens, pipelineOutputTokens) : null),
    [isPipelineRun, pipelineInputTokens, pipelineOutputTokens]
  );

  // Default selected trace node to root when tree becomes available
  useEffect(() => {
    if (!traceTree) return;
    if (!selectedTraceNodeId || !traceTree.flatMap[selectedTraceNodeId]) {
      setSelectedTraceNodeId(traceTree.rootNode.id);
    }
  }, [traceTree, selectedTraceNodeId]);

  const selectedTraceNode = traceTree
    ? traceTree.flatMap[selectedTraceNodeId] || traceTree.rootNode
    : null;
  const selectedMember =
    selectedTraceNode && selectedTraceNode.type === 'member'
      ? selectedTraceNode.source?.member || null
      : null;
  const selectedTool =
    selectedTraceNode && selectedTraceNode.type === 'tool'
      ? selectedTraceNode.source?.tool || null
      : null;
  const isToolSelected = !!selectedTool;
  const isNodeSelected = selectedTraceNode && selectedTraceNode.type === 'node';
  const isNodeContext = isNodeSelected || metricType === 'node';

  // Workflow Vars tab allowed only for:
  // - pipeline run (root metric type is pipeline)
  // - or when the currently selected trace node is a node
  const hasWorkflowVarsTab = useMemo(() => {
    if (isToolSelected) return false;
    if (metricType === 'pipeline') return true;
    const selType = selectedTraceNode?.type || null;
    return selType === 'node';
  }, [isToolSelected, selectedTraceNode?.type, metricType]);

  const toolParentMember =
    selectedTraceNode && selectedTraceNode.type === 'tool'
      ? selectedTraceNode.source?.parentMember || null
      : null;

  const toolParentRun =
    selectedTraceNode && selectedTraceNode.type === 'tool'
      ? selectedTraceNode.source?.parentRun || null
      : null;

  // When a tool is selected, treat its parent member (if any) as the
  // active member context for displaying parent agent request/response
  // and conversation.
  const memberContext = selectedMember || toolParentMember;

  const memberInputContent =
    memberContext &&
      memberContext.input &&
      typeof memberContext.input === 'object'
      ? memberContext.input.input_content
      : null;

  let memberFinalContent = null;
  if (memberContext) {
    if (typeof memberContext.content === 'string') {
      memberFinalContent = memberContext.content;
    } else if (memberContext.content && typeof memberContext.content === 'object') {
      if (memberContext.content.summary) {
        memberFinalContent = memberContext.content.summary;
      }
    }
  }

  const memberMainMarkdown = (() => {
    if (!memberFinalContent) return null;
    if (typeof memberFinalContent === 'string') return memberFinalContent;
    try {
      return JSON.stringify(memberFinalContent, null, 2);
    } catch (e) {
      return String(memberFinalContent);
    }
  })();

  const memberMessages = Array.isArray(memberContext?.messages)
    ? memberContext.messages
    : [];

  const memberSortedMessages = memberMessages.length
    ? [...memberMessages].sort((a, b) => {
      const da = a && a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b && b.created_at ? new Date(b.created_at).getTime() : 0;
      return da - db;
    })
    : [];

  const memberMetrics = memberContext && memberContext.metrics ? memberContext.metrics : null;
  const toolMetrics = selectedTool && selectedTool.metrics ? selectedTool.metrics : null;

  const effectiveMetrics = memberMetrics || toolMetrics || metrics;

  const effectiveTotalTokens =
    memberMetrics && memberMetrics.total_tokens != null
      ? memberMetrics.total_tokens
      : toolMetrics && toolMetrics.total_tokens != null
        ? toolMetrics.total_tokens
        : totalTokens;

  // Helper function to fetch all children with pagination for a given parent run ID
  const fetchAllChildrenPaginated = async (parentRunId, cancelled) => {
    let allChildren = [];
    let skip = 0;
    const limit = 50;
    let hasMore = true;

    while (hasMore && !cancelled.current) {
      try {
        const response = await getChildMetricRuns(parentRunId, { filter: {}, skip, limit });
        if (cancelled.current) return [];

        const array = Array.isArray(response)
          ? response
          : response && Array.isArray(response.items)
            ? response.items
            : response && Array.isArray(response.data)
              ? response.data
              : [];

        allChildren = [...allChildren, ...array];

        // If we got less than the limit, we've fetched all records
        if (array.length < limit) {
          hasMore = false;
        } else {
          skip += limit;
        }
      } catch (e) {
        console.error(`Failed to fetch children for ${parentRunId}:`, e);
        hasMore = false; // Stop on error
      }
    }

    return allChildren;
  };

  // Recursive function to fetch all nested children
  const fetchNestedChildren = async (runId, cancelled, nestedMap) => {
    const children = await fetchAllChildrenPaginated(runId, cancelled);
    if (cancelled.current || children.length === 0) return;

    // Store children in the map
    nestedMap[runId] = children;

    // Recursively fetch children for each child
    await Promise.all(
      children.map(async (child) => {
        const childId = child.run_id || child.id;
        if (childId) {
          await fetchNestedChildren(childId, cancelled, nestedMap);
        }
      })
    );
  };

  useEffect(() => {
    if (!run || !isPipelineRun) return;
    const parentRunId = run.run_id || run.id;
    if (!parentRunId) return;

    const cancelled = { current: false };

    const fetchAllChildRuns = async () => {
      try {
        setChildRunsLoading(true);
        setChildRunsError(null);
        
        // Fetch first level children with pagination
        const allChildRuns = await fetchAllChildrenPaginated(parentRunId, cancelled);
        
        if (cancelled.current) return;
        setChildRuns(allChildRuns);

        // Fetch nested children recursively for each child run
        const nestedMap = {};
        await Promise.all(
          allChildRuns.map(async (child) => {
            const childId = child.run_id || child.id;
            if (childId) {
              await fetchNestedChildren(childId, cancelled, nestedMap);
            }
          })
        );

        if (!cancelled.current) {
          setNestedChildrenMap(nestedMap);
        }
      } catch (e) {
        if (!cancelled.current) {
          console.error('Failed to fetch child metric runs', e);
          setChildRunsError('Failed to load pipeline steps');
        }
      } finally {
        if (!cancelled.current) {
          setChildRunsLoading(false);
        }
      }
    };

    fetchAllChildRuns();

    return () => {
      cancelled.current = true;
    };
  }, [run, isPipelineRun]);

  const throughputTokensPerSecond = useMemo(() => {
    const durationSeconds =
      typeof effectiveMetrics.duration === 'number' ? effectiveMetrics.duration : null;
    const tokens = effectiveTotalTokens;
    if (!durationSeconds || !tokens) return null;
    if (durationSeconds <= 0) return null;
    return tokens / durationSeconds;
  }, [effectiveMetrics, effectiveTotalTokens]);

  const ttftSparkData = useMemo(
    () => buildScalarSparklineData(effectiveMetrics.time_to_first_token),
    [effectiveMetrics]
  );

  const durationSparkData = useMemo(() => {
    const durSeconds =
      typeof effectiveMetrics.duration === 'number'
        ? effectiveMetrics.duration
        : typeof run?.duration_ms === 'number'
          ? run.duration_ms / 1000
          : null;
    return buildScalarSparklineData(durSeconds);
  }, [effectiveMetrics, run?.duration_ms]);

  const throughputSparkData = useMemo(
    () => buildScalarSparklineData(throughputTokensPerSecond),
    [throughputTokensPerSecond]
  );

  const sortedMessages = useMemo(() => {
    if (messages.length === 0) return [];
    return [...messages].sort((a, b) => {
      const da = a && a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b && b.created_at ? new Date(b.created_at).getTime() : 0;
      return da - db; // ascending: oldest first
    });
  }, [messages]);

  const titlePrefix = useMemo(() => {
    if (metricType === 'agent') return 'Agent Run';
    if (metricType === 'planner') return 'Planner Run';
    if (metricType === 'pipeline') return 'Pipeline Run';
    return 'Metric Run';
  }, [metricType]);

  const headerIcon = useMemo(() => {
    if (metricType === 'agent') return Bot;
    if (metricType === 'planner') return Workflow;
    if (metricType === 'pipeline') return Network;
    return Activity;
  }, [metricType]);

  // If a node is selected, hide conversation/system-prompt tabs; ensure activeTab is valid
  useEffect(() => {
    if (isNodeSelected && (activeTab === 'conversation' || activeTab === 'system-prompt')) {
      setActiveTab('request-response');
    }
  }, [isNodeSelected, activeTab]);

  // If a tool is selected, switch to tool tabs; otherwise ensure we're on non-tool tab
  useEffect(() => {
    const toolTabs = new Set(['tool-request', 'tool-task', 'tool-response', 'tool-metrics']);
    if (isToolSelected) {
      if (!toolTabs.has(activeTab)) {
        setActiveTab('tool-request');
      }
    } else if (toolTabs.has(activeTab)) {
      setActiveTab('request-response');
    }
  }, [isToolSelected, activeTab]);

  // Ensure Workflow Vars tab isn't active when not allowed
  useEffect(() => {
    if (!hasWorkflowVarsTab && activeTab === 'workflow-vars') {
      setActiveTab('request-response');
    }
  }, [hasWorkflowVarsTab, activeTab]);

  // Ensure Node Metrics tab isn't active when not in node context
  useEffect(() => {
    if (!isNodeContext && activeTab === 'node-metrics') {
      setActiveTab('request-response');
    }
  }, [isNodeContext, activeTab]);

  const HeaderIconComponent = headerIcon;

  const renderStatusBadgeStatus = (status) => {
    if (!status) return 'default';
    const s = String(status).toLowerCase();
    if (['completed', 'success', 'ok'].includes(s)) return 'active';
    if (['failed', 'error'].includes(s)) return 'error';
    if (['running', 'in_progress'].includes(s)) return 'running';
    return 'default';
  };

  const finalAssistantContent = useMemo(() => {
    if (runData.content && typeof runData.content === 'string') return runData.content;
    if (runData.content && typeof runData.content === 'object') {
      if (runData.content.summary) return runData.content.summary;
    }
    const reversed = [...messages].reverse();
    const lastAssistant = reversed.find((m) => m.role === 'assistant');
    return lastAssistant?.content || null;
  }, [runData, messages]);

  const mainMarkdown = useMemo(() => {
    if (!finalAssistantContent) return null;
    if (typeof finalAssistantContent === 'string') return finalAssistantContent;
    try {
      return JSON.stringify(finalAssistantContent, null, 2);
    } catch (e) {
      return String(finalAssistantContent);
    }
  }, [finalAssistantContent]);

  const pipelineSummaryText = useMemo(() => {
    if (!isPipelineRun) return null;
    let summary = null;
    if (runData && typeof runData.content === 'string') {
      summary = runData.content;
    } else if (runData && runData.content && typeof runData.content === 'object' && runData.content.summary) {
      summary = runData.content.summary;
    } else if (runData && runData.summary) {
      summary = runData.summary;
    }
    if (!summary) return null;
    if (typeof summary === 'string') return summary;
    try {
      return JSON.stringify(summary, null, 2);
    } catch (e) {
      return String(summary);
    }
  }, [isPipelineRun, runData]);

  const pipelineKindLabel = metricType === 'pipeline' ? 'Pipeline' : metricType === 'planner' ? 'Planner' : 'Run';

  const isPipelineRootStepSelected = isPipelineRun
    ? !selectedTraceNode || selectedTraceNode.type === 'pipeline'
    : false;

  const childRun =
    isPipelineRun &&
      selectedTraceNode &&
      selectedTraceNode.source &&
      selectedTraceNode.source.kind === 'child-run'
      ? selectedTraceNode.source.run
      : null;

  // For planner runs, when no childRun is selected, treat the planner root run
  // itself as the "child" context for the center panel (Request & Response,
  // Conversation, etc.). For true pipeline runs we still distinguish the
  // pipeline root summary card vs child runs.
  const showPipelineRootPanel =
    isPipelineRun && !isPlannerRun && !isAgentRun && isPipelineRootStepSelected && !childRun && !memberContext;

  const useRootAsChildContext = isPipelineRun && (isPlannerRun || isAgentRun) && !childRun;

  const rawChildRunData =
    childRun && childRun.run_data && typeof childRun.run_data === 'object' ? childRun.run_data : {};

  const childRunData = useRootAsChildContext ? runData : rawChildRunData;
  const childMessages = Array.isArray(childRunData.messages) ? childRunData.messages : [];
  const childMetrics = useRootAsChildContext ? metrics : childRunData.metrics || {};
  const childTotalTokens =
    childMetrics.total_tokens != null
      ? childMetrics.total_tokens
      : useRootAsChildContext
        ? totalTokens
        : childRun?.total_tokens;

  const memberTokenPieData = useMemo(() => {
    const fromChild =
      childRunData && Array.isArray(childRunData.member_responses)
        ? childRunData.member_responses
        : null;
    const members = fromChild && fromChild.length
      ? fromChild
      : Array.isArray(runData.member_responses)
        ? runData.member_responses
        : [];
    return buildMemberTokenPieData(members);
  }, [childRunData, runData]);

  const requestResponseContextLabel = (() => {
    if (memberContext) {
      return memberContext.agent_name || memberContext.agent_id || 'Member run';
    }
    if (childRun) {
      return (
        childRun.metric_type_name ||
        childRun.agent_name ||
        childRun.planner_name ||
        childRun.metric_type ||
        'Child run'
      );
    }
    if (toolParentRun) {
      return (
        toolParentRun.metric_type_name ||
        toolParentRun.agent_name ||
        toolParentRun.planner_name ||
        toolParentRun.metric_type ||
        'Run'
      );
    }
    if (isPlannerRun) {
      return pipelineName || run?.metric_type_name || 'Planner run';
    }
    if (isAgentRun) {
      return run?.agent_name || run?.metric_type_name || 'Agent run';
    }
    if (!isPipelineRun) {
      return run?.metric_type_name || run?.agent_name || run?.metric_type || 'Run';
    }
    return null;
  })();

  let childThroughputTokensPerSecond = null;
  if (childRun || useRootAsChildContext) {
    const durationSeconds =
      typeof childMetrics.duration === 'number'
        ? childMetrics.duration
        : childRun && typeof childRun.duration_ms === 'number'
          ? childRun.duration_ms / 1000
          : null;
    if (durationSeconds && durationSeconds > 0 && childTotalTokens) {
      childThroughputTokensPerSecond = childTotalTokens / durationSeconds;
    }
  }

  const childTtftSparkData =
    childRun || useRootAsChildContext
      ? buildScalarSparklineData(childMetrics.time_to_first_token)
      : null;

  const childDurationSparkData =
    childRun || useRootAsChildContext
      ? (() => {
        const durSeconds =
          typeof childMetrics.duration === 'number'
            ? childMetrics.duration
            : childRun && typeof childRun.duration_ms === 'number'
              ? childRun.duration_ms / 1000
              : null;
        return buildScalarSparklineData(durSeconds);
      })()
      : null;

  const childThroughputSparkData =
    childRun || useRootAsChildContext
      ? buildScalarSparklineData(childThroughputTokensPerSecond)
      : null;

  const hasChildRunContextForRight =
    isPipelineRun && (childRun || useRootAsChildContext) && !memberContext && !selectedTool;

  const rightMetrics = hasChildRunContextForRight ? childMetrics : effectiveMetrics;
  const rightTotalTokens = hasChildRunContextForRight ? childTotalTokens : effectiveTotalTokens;

  const rightThroughputTokensPerSecond = hasChildRunContextForRight
    ? childThroughputTokensPerSecond
    : throughputTokensPerSecond;

  const rightTtftSparkData = hasChildRunContextForRight ? childTtftSparkData : ttftSparkData;
  const rightDurationSparkData = hasChildRunContextForRight ? childDurationSparkData : durationSparkData;
  const rightThroughputSparkData = hasChildRunContextForRight
    ? childThroughputSparkData
    : throughputSparkData;

  const rightInputTokens =
    (rightMetrics && rightMetrics.input_tokens != null
      ? rightMetrics.input_tokens
      : hasChildRunContextForRight && childRun
        ? childRun.input_tokens
        : run?.input_tokens);

  const rightOutputTokens =
    (rightMetrics && rightMetrics.output_tokens != null
      ? rightMetrics.output_tokens
      : hasChildRunContextForRight && childRun
        ? childRun.output_tokens
        : run?.output_tokens);

  const nodeTokenDonutData = useMemo(
    () => (isNodeContext ? buildTokenDonutData(rightInputTokens, rightOutputTokens) : null),
    [isNodeContext, rightInputTokens, rightOutputTokens]
  );

  // Build agent-wise token usage for a selected node from its nested children (child metric runs)
  const nodeChildrenForAgent = useMemo(() => {
    if (!isNodeContext) return null;
    let targetRunId = null;
    if (selectedTraceNode && selectedTraceNode.source && selectedTraceNode.source.kind === 'child-run') {
      const r = selectedTraceNode.source.run;
      targetRunId = (r && (r.run_id || r.id)) || null;
    } else if (metricType === 'node') {
      targetRunId = (run && (run.run_id || run.id)) || null;
    }
    if (!targetRunId) return null;
    const arr = nestedChildrenMap && nestedChildrenMap[targetRunId];
    return Array.isArray(arr) ? arr : null;
  }, [isNodeContext, selectedTraceNode, metricType, run, nestedChildrenMap]);

  const nodeAgentPieData = useMemo(() => {
    const children = nodeChildrenForAgent;
    if (!Array.isArray(children) || children.length === 0) return null;
    const baseColors = ['#4f46e5', '#f97316', '#10b981', '#0ea5e9', '#ec4899', '#22c55e', '#6366f1', '#facc15'];
    const labels = [];
    const data = [];
    const backgroundColor = [];

    children.forEach((child, idx) => {
      if (!child) return;
      const rd = child.run_data && typeof child.run_data === 'object' ? child.run_data : {};
      const m = rd.metrics || {};
      const tokens = (typeof m.total_tokens === 'number' ? m.total_tokens : child.total_tokens) || 0;
      if (tokens <= 0) return;
      const label = child.metric_type_name || child.agent_name || child.planner_name || rd.model || `Child ${idx + 1}`;
      labels.push(label);
      data.push(tokens);
      backgroundColor.push(baseColors[idx % baseColors.length]);
    });

    if (!labels.length) return null;
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 0,
        },
      ],
    };
  }, [nodeChildrenForAgent]);

  const rightCacheReadTokens =
    rightMetrics && rightMetrics.cache_read_tokens != null
      ? rightMetrics.cache_read_tokens
      : metrics.cache_read_tokens;

  const childSortedMessages =
    childRun || useRootAsChildContext
      ? [...childMessages].sort((a, b) => {
        const da = a && a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b && b.created_at ? new Date(b.created_at).getTime() : 0;
        return da - db;
      })
      : [];

  let childFinalAssistantContent = null;
  if (childRun || useRootAsChildContext) {
    if (childRunData.content && typeof childRunData.content === 'string') {
      childFinalAssistantContent = childRunData.content;
    } else if (childRunData.content && typeof childRunData.content === 'object') {
      if (childRunData.content.summary) {
        childFinalAssistantContent = childRunData.content.summary;
      }
    }
    if (!childFinalAssistantContent) {
      const reversed = [...childMessages].reverse();
      const lastAssistant = reversed.find((m) => m.role === 'assistant');
      childFinalAssistantContent = lastAssistant?.content || null;
    }
  }

  const childMainMarkdown = (() => {
    if (!childFinalAssistantContent) return null;
    if (typeof childFinalAssistantContent === 'string') return childFinalAssistantContent;
    try {
      return JSON.stringify(childFinalAssistantContent, null, 2);
    } catch (e) {
      return String(childFinalAssistantContent);
    }
  })();

  const toolMainMarkdown = (() => {
    if (!selectedTool) return null;
    const value =
      selectedTool.result != null
        ? selectedTool.result
        : selectedTool.output != null
          ? selectedTool.output
          : null;
    if (!value) return null;
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  })();

  const toolRequestText = (() => {
    if (!selectedTool) return null;
    const args = selectedTool.tool_args || selectedTool.input || null;
    if (!args) return null;
    if (typeof args === 'string') return args;
    try {
      return JSON.stringify(args, null, 2);
    } catch (e) {
      return String(args);
    }
  })();

  const toolArgsJsonText = useMemo(() => {
    if (!selectedTool) return null;
    const args = selectedTool.tool_args || selectedTool.input || null;
    if (args == null) return null;
    if (typeof args === 'string') {
      try {
        const parsed = JSON.parse(args);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return args;
      }
    }
    try {
      return JSON.stringify(args, null, 2);
    } catch (e) {
      return String(args);
    }
  }, [selectedTool]);

  // Determine which metric run's request/response to show based on selection
  const rrMetricRun = useMemo(() => {
    if (selectedTraceNode?.type === 'tool') return selectedTraceNode.source?.parentRun || run;
    if (selectedTraceNode?.type === 'member') return selectedTraceNode.source?.parentRun || run;
    if (selectedTraceNode?.source?.kind === 'child-run') return selectedTraceNode.source.run;
    if (selectedTraceNode?.type === 'pipeline') return run;
    return run;
  }, [selectedTraceNode, run]);

  const rrRunData = rrMetricRun && typeof rrMetricRun.run_data === 'object' ? rrMetricRun.run_data : {};

  const workflowVars = useMemo(() => {
    if (!rrRunData || typeof rrRunData !== 'object') return null;
    const ss = rrRunData.session_state && typeof rrRunData.session_state === 'object' ? rrRunData.session_state : null;
    const vars = ss && (ss.workflow_var != null ? ss.workflow_var : (ss.workflow_vars != null ? ss.workflow_vars : null));
    return vars || null;
  }, [rrRunData]);

  const rrMetricType = rrMetricRun?.metric_type || rrMetricRun?.metricType || null;
  const hasReasoningTab = useMemo(() => {
    const t = String(rrMetricType || metricType || '').toLowerCase();
    return t === 'agent' || t === 'planner';
  }, [rrMetricType, metricType]);

  const reasoningSteps = useMemo(() => {
    if (!hasReasoningTab) return [];
    const steps = rrRunData?.reasoning_steps;
    return Array.isArray(steps) ? steps : [];
  }, [hasReasoningTab, rrRunData]);

  const [expandedSteps, setExpandedSteps] = useState(() => new Set());

  useEffect(() => {
    const n = reasoningSteps.length;
    if (n === 0) {
      setExpandedSteps(new Set());
      return;
    }
    const ns = new Set();
    ns.add(0);
    ns.add(n - 1);
    setExpandedSteps(ns);
  }, [reasoningSteps]);

  const stepsCount = reasoningSteps.length;
  const avgConfidence = useMemo(() => {
    if (stepsCount === 0) return null;
    let sum = 0;
    let cnt = 0;
    for (const s of reasoningSteps) {
      if (typeof s?.confidence === 'number' && Number.isFinite(s.confidence)) {
        const c = Math.max(0, Math.min(1, s.confidence));
        sum += c;
        cnt += 1;
      }
    }
    if (cnt === 0) return null;
    return sum / cnt;
  }, [reasoningSteps, stepsCount]);

  const outcomeLabel = useMemo(() => {
    if (stepsCount === 0) return null;
    const last = reasoningSteps[stepsCount - 1];
    const na = String(last?.next_action || '').toLowerCase();
    if (na === 'final_answer') return 'Final answer';
    if (!na) return null;
    return na;
  }, [reasoningSteps, stepsCount]);

  const allExpanded = stepsCount > 0 && expandedSteps.size === stepsCount;
  const toggleStep = (idx) => {
    setExpandedSteps((prev) => {
      const ns = new Set(prev);
      if (ns.has(idx)) ns.delete(idx); else ns.add(idx);
      return ns;
    });
  };
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedSteps(new Set());
    } else {
      const ns = new Set();
      for (let i = 0; i < stepsCount; i++) ns.add(i);
      setExpandedSteps(ns);
    }
  };


  useEffect(() => {
    if (!hasReasoningTab && activeTab === 'reasoning') {
      setActiveTab('request-response');
    }
  }, [hasReasoningTab, activeTab]);


  const renderTraceNode = (node) => {
    const variant = getTraceStatusVariant(node.status);
    const statusColors = getTraceStatusColors(variant);
    const iconStyle = getTraceNodeIconStyle(node.type);
    const metaParts = [];

    if (typeof node.durationSeconds === 'number') {
      const durLabel = formatShortDurationSeconds(node.durationSeconds);
      if (durLabel) metaParts.push(durLabel);
    }
    if (typeof node.totalTokens === 'number') {
      metaParts.push(`${node.totalTokens} tokens`);
    }
    if (node.meta?.resultPreview) {
      metaParts.push(node.meta.resultPreview);
    }
    const metaText = metaParts.join(' · ');

    return (
      <TraceNodeWrapper key={node.id}>
        <TraceNodeHeaderButton
          type="button"
          $selected={selectedTraceNode && selectedTraceNode.id === node.id}
          onClick={() => setSelectedTraceNodeId(node.id)}
        >
          <TraceNodeLeft>
            <TraceNodeIcon style={{ backgroundColor: iconStyle.backgroundColor, color: iconStyle.color }}>
              {iconStyle.label}
            </TraceNodeIcon>
            <TraceNodeText>
              <TraceNodeName>{node.label}</TraceNodeName>
              {metaText && <TraceNodeMeta>{metaText}</TraceNodeMeta>}
            </TraceNodeText>
          </TraceNodeLeft>
          {node.status && (
            <TraceNodeStatusBadge style={{ background: statusColors.background, color: statusColors.color }}>
              {String(node.status).toUpperCase()}
            </TraceNodeStatusBadge>
          )}
        </TraceNodeHeaderButton>
        {Array.isArray(node.children) && node.children.length > 0 && (
          <TraceChildren>
            {node.children.map((child) => renderTraceNode(child))}
          </TraceChildren>
        )}
      </TraceNodeWrapper>
    );
  };

  return (
    <PageContainer>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <PageHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <PageIcon>
              <HeaderIconComponent size={18} />
            </PageIcon>
            <div>
              <PageTitle>
                {titlePrefix}
                {' '}
                –
                {' '}
                {isPipelineRun
                  ? pipelineName ||
                  run?.metric_type_name ||
                  run?.metric_type_code ||
                  run?.metric_type ||
                  id
                  : run?.metric_type_name || run?.agent_name || run?.metric_type_code || id}
                {isPipelineRun && pipelineBusinessValue ? ` · ${pipelineBusinessValue}` : ''}
              </PageTitle>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.1rem', display: 'none' }}>
                Run ID: {run?.run_id || 'n/a'}
                {' '}
                · User: {run?.user_id || 'n/a'}
                {' '}
                · Session: {run?.session_id || runData.session_id || 'n/a'}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span>Started: {formatTimestamp(run?.started_at)}</span>
                <span style={{ width: 4, height: 4, borderRadius: '999px', background: '#d1d5db' }} />
                <span>Duration: {formatDurationMs(run?.duration_ms)}</span>
                <span style={{ width: 4, height: 4, borderRadius: '999px', background: '#d1d5db' }} />
                <StatusBadge status={renderStatusBadgeStatus(run?.status)}>{run?.status}</StatusBadge>
              </div>
              {run?.parent_run_context && run.parent_run_context.entity_type === 'pipeline' && (
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.1rem' }}>
                  Part of pipeline {run.parent_run_context.entity_name} (run {run.parent_run_context.run_id})
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.8rem' }}>
            {run && (
              <>
                <div style={{ display: 'none' }}>
                  <div>Started: {formatTimestamp(run.started_at)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span>Duration: {formatDurationMs(run.duration_ms)}</span>
                    <span style={{ width: 3, height: 3, borderRadius: '999px', background: '#d1d5db' }} />
                    <StatusBadge status={renderStatusBadgeStatus(run.status)}>{run.status}</StatusBadge>
                  </div>
                </div>
                <div style={{ display: 'none', alignItems: 'center', gap: '0.4rem' }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '999px',
                      background: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#374151'
                    }}
                  >
                    {run.user_id ? run.user_id.slice(0, 2).toUpperCase() : 'NA'}
                  </div>
                  <div>{run.user_id || 'n/a'}</div>
                </div>
                {totalTokens != null && (
                  <HeaderMetricPill>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '999px',
                        background: 'rgba(37, 99, 235, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Clock size={14} color="#2563eb" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <HeaderMetricValue>{totalTokens.toLocaleString()}</HeaderMetricValue>
                      <HeaderMetricLabel>Total Token Used</HeaderMetricLabel>
                    </div>
                  </HeaderMetricPill>
                )}
              </>
            )}
          </div>
        </PageHeader>

        {loading && (
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Loading metric run details...
          </div>
        )}
        {error && !loading && (
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#FF3B30' }}>{error}</div>
        )}

        {!loading && !error && run && (
          <LayoutContainer>
            {isPipelineRun && (
              <>
                <SidebarColumn>
                  <Card>
                    <CardTitle style={{ fontWeight: 600 }}>Execution hierarchy</CardTitle>
                    <CardContent>
                      {traceTree && traceTree.rootNode ? (
                        <TraceTreeContainer>
                          {renderTraceNode(traceTree.rootNode)}
                        </TraceTreeContainer>
                      ) : (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          No hierarchy data available for this pipeline run.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardTitle style={{ fontWeight: 600 }}>User & Session</CardTitle>
                    <CardContent>
                      <FieldRow>
                        <FieldLabel>User</FieldLabel>
                        <FieldValue>{run.user_id || 'n/a'}</FieldValue>
                      </FieldRow>
                      <FieldRow>
                        <FieldLabel>Session ID</FieldLabel>
                        <FieldValue>{run.session_id || runData.session_id || 'n/a'}</FieldValue>
                      </FieldRow>
                      {run.parent_run_context && (
                        <FieldRow>
                          <FieldLabel>Parent run</FieldLabel>
                          <FieldValue>{run.parent_run_context.run_id}</FieldValue>
                        </FieldRow>
                      )}
                    </CardContent>
                  </Card>

                  {/* <Card>
                    <CardTitle style={{ fontWeight: 600 }}>
                      {metricType === 'pipeline'
                        ? 'Pipeline configuration'
                        : metricType === 'planner'
                          ? 'Planner configuration'
                          : 'Agent configuration'}
                    </CardTitle>
                    <CardContent>
                      <FieldRow>
                        <FieldLabel>Name</FieldLabel>
                        <FieldValue>{pipelineName || 'n/a'}</FieldValue>
                      </FieldRow>
                      <FieldRow>
                        <FieldLabel>Version</FieldLabel>
                        <FieldValue>{run.pipeline_version || runData.pipeline_version || 'n/a'}</FieldValue>
                      </FieldRow>
                    </CardContent>
                  </Card> */}
                </SidebarColumn>

                {showPipelineRootPanel ? (
                  <>
                    <MainColumn>
                      <PerformanceCard style={{ marginBottom: '1rem', border: 'none', boxShadow: 'none' }}>
                        <GaugesContainer>
                          <GaugeSection>
                            <div style={{ width: 172, height: 172, textAlign: 'center' }}>
                              {pipelineTokenDonutData ? (
                                <>
                                
                                  <Pie data={pipelineTokenDonutData} options={tokenDonutOptions} />
                                  <PerformanceCardLabel>Pipeline Token Usage</PerformanceCardLabel>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      width: '100%',
                                      borderRadius: '999px',
                                      border: '1px dashed #e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      color: 'var(--text-secondary)'
                                    }}
                                  >
                                    No data
                                  </div>
                                  <PerformanceCardLabel>Pipeline Token Usage</PerformanceCardLabel>
                                </>
                              )}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <PerformanceCardValue>{(pipelineInputTokens || 0) + (pipelineOutputTokens || 0) > 0 ? ((pipelineInputTokens || 0) + (pipelineOutputTokens || 0)).toLocaleString() : (totalTokens || 0).toLocaleString()}</PerformanceCardValue>

                              <PerformanceCardStats style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.75rem' }}>
                                <PerformanceCardStat>
                                  <span style={{ color: '#2563eb' }}>●</span>
                                  <span>Input tokens: {(pipelineInputTokens || 0).toLocaleString()}</span>
                                </PerformanceCardStat>
                                <PerformanceCardStat>
                                  <span style={{ color: '#f97316' }}>●</span>
                                  <span>Output tokens: {(pipelineOutputTokens || 0).toLocaleString()}</span>
                                </PerformanceCardStat>
                              </PerformanceCardStats>
                              {/* <ViewDetailsLink>View Details →</ViewDetailsLink> */}
                            </div>
                          </GaugeSection>

                          <GaugeSection>
                            <div style={{ width: 172, height: 172, textAlign: 'center' }}>
                              {pipelineStepTokenPieData ? (
                                <>
                                  <Pie data={pipelineStepTokenPieData} options={tokenDonutOptions} />
                                  <PerformanceCardLabel>Agent-wise Token Usage</PerformanceCardLabel>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      width: '100%',
                                      borderRadius: '999px',
                                      border: '1px dashed #e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      color: 'var(--text-secondary)'
                                    }}
                                  >
                                    No data
                                  </div>
                                  <PerformanceCardLabel>Agent-wise Token Usage</PerformanceCardLabel>
                                </>
                              )}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <PerformanceCardValue>{totalTokens ? totalTokens.toLocaleString() : 'N/A'}</PerformanceCardValue>

                              <PerformanceCardStats style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.75rem' }}>
                                {pipelineStepTokenPieData && pipelineStepTokenPieData.labels.map((label, idx) => (
                                  <PerformanceCardStat key={idx}>
                                    <span style={{ color: pipelineStepTokenPieData.datasets[0].backgroundColor[idx] }}>●</span>
                                    <span>{label}: {pipelineStepTokenPieData.datasets[0].data[idx].toLocaleString()}</span>
                                  </PerformanceCardStat>
                                ))}
                              </PerformanceCardStats>
                              {/* {pipelineStepTokenPieData && pipelineStepTokenPieData.labels.length > 3 && (
                                  <ViewDetailsLink>View Details →</ViewDetailsLink>
                                )} */}
                            </div>
                          </GaugeSection>
                        </GaugesContainer>
                      </PerformanceCard>

                      <Card>
                        <CardContent>
                          <PreviewHeader>
                            <div>
                              <PreviewLabel>Preview · Selected step</PreviewLabel>
                              <PreviewTitle>
                                {selectedStep
                                  ? `${getStepTypeLabel(selectedStep.type)} – ${selectedStep.label}`
                                  : pipelineName}
                              </PreviewTitle>
                              <PreviewSubtitle>
                                {pipelineName || pipelineKindLabel}
                                {pipelineCaseId ? ` · Claim ${pipelineCaseId}` : ''}
                                {pipelineMemberLabel ? ` · ${pipelineMemberLabel}` : ''}
                              </PreviewSubtitle>
                            </div>
                            <PreviewChips>
                              <StatusBadge status={renderStatusBadgeStatus(selectedStep?.status || run.status)}>
                                {selectedStep?.status || run.status}
                              </StatusBadge>
                              {runData.model && <PreviewChip>{runData.model}</PreviewChip>}
                              {typeof (selectedStep?.tokensIn ?? metrics.input_tokens ?? run?.input_tokens) === 'number' && (
                                <PreviewChip>
                                  {safeNumber(selectedStep?.tokensIn ?? metrics.input_tokens ?? run?.input_tokens)} in ·{' '}
                                  {safeNumber(selectedStep?.tokensOut ?? metrics.output_tokens ?? run?.output_tokens)} out
                                </PreviewChip>
                              )}
                              {selectedStep?.durationMs || run.duration_ms ? (
                                <PreviewChip>
                                  {formatDurationMs(
                                    selectedStep?.durationMs ||
                                    (typeof metrics.duration === 'number' ? metrics.duration * 1000 : run.duration_ms)
                                  )}
                                </PreviewChip>
                              ) : null}
                            </PreviewChips>
                          </PreviewHeader>

                          <Tabs style={{ marginTop: '0.75rem' }}>
                            <TabButton active={pipelineTab === 'summary'} onClick={() => setPipelineTab('summary')}>
                              Summary
                            </TabButton>
                            <TabButton active={pipelineTab === 'request'} onClick={() => setPipelineTab('request')}>
                              Request
                            </TabButton>
                            <TabButton active={pipelineTab === 'response'} onClick={() => setPipelineTab('response')}>
                              Response
                            </TabButton>
                            <TabButton active={pipelineTab === 'metrics'} onClick={() => setPipelineTab('metrics')}>
                              Metrics
                            </TabButton>
                            {metricType === 'pipeline' && (
                              <TabButton active={pipelineTab === 'workflow-vars'} onClick={() => setPipelineTab('workflow-vars')}>
                                Workflow Vars
                              </TabButton>
                            )}
                            {/* <TabButton active={pipelineTab === 'logs'} onClick={() => setPipelineTab('logs')}>
                              Logs
                            </TabButton> */}
                          </Tabs>

                          <TabPanel>
                            {pipelineTab === 'summary' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div>
                                  <div style={{ marginBottom: 4, fontWeight: 500 }}>{pipelineKindLabel} summary</div>
                                  {pipelineSummaryText ? (
                                    <MarkdownContainer>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {pipelineSummaryText}
                                      </ReactMarkdown>
                                    </MarkdownContainer>
                                  ) : (
                                    <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                                      (no {pipelineKindLabel.toLowerCase()} summary available)
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {metricType === 'pipeline' && pipelineTab === 'workflow-vars' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Workflow variables for this pipeline run (session_state.workflow_vars)
                                </div>
                                <JsonBlock>
                                  {(() => {
                                    const ss = runData && typeof runData.session_state === 'object' ? runData.session_state : null;
                                    const vars = ss && (ss.workflow_vars != null ? ss.workflow_vars : (ss.workflow_var != null ? ss.workflow_var : null));
                                    return vars ? JSON.stringify(vars, null, 2) : '// No workflow variables available for this pipeline run.';
                                  })()}
                                </JsonBlock>
                              </div>
                            )}

                            {pipelineTab === 'request' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Request taken from run_data.input.input_content when available.
                                </div>
                                {runData.input && typeof runData.input.input_content === 'string' ? (
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: 8,
                                      border: '1px solid #e5e7eb',
                                      padding: '8px 10px',
                                      fontSize: '0.85rem',
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {runData.input.input_content}
                                  </div>
                                ) : (
                                  <JsonBlock>
                                    {runData.request
                                      ? JSON.stringify(runData.request, null, 2)
                                      : runData.input
                                        ? JSON.stringify(runData.input, null, 2)
                                        : '// No structured request payload available for this run.'}
                                  </JsonBlock>
                                )}
                              </div>
                            )}

                            {pipelineTab === 'response' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Final response from this run (based on run_data.content when available).
                                </div>
                                {runData && typeof runData.content === 'string' ? (
                                  <MarkdownContainer>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {runData.content}
                                    </ReactMarkdown>
                                  </MarkdownContainer>
                                ) : runData && runData.content ? (
                                  <JsonBlock>{JSON.stringify(runData.content, null, 2)}</JsonBlock>
                                ) : runData && runData.response ? (
                                  <JsonBlock>{JSON.stringify(runData.response, null, 2)}</JsonBlock>
                                ) : (
                                  <JsonBlock>{'// No response content available for this run.'}</JsonBlock>
                                )}
                              </div>
                            )}

                            {pipelineTab === 'metrics' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                                  Token usage per step / agent (based on total tokens).
                                </div>
                                {pipelineStepTokenPieData ? (
                                  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
                                    <div style={{ width: 220, height: 220 }}>
                                      <Pie data={pipelineStepTokenPieData} options={tokenDonutOptions} />
                                    </div>
                                    <div style={{ fontSize: '0.8rem', flex: 1 }}>
                                      {pipelineStepTokenPieData.labels.map((label, idx) => {
                                        const value = pipelineStepTokenPieData.datasets[0].data[idx];
                                        return (
                                          <FieldRow key={label + idx}>
                                            <FieldLabel>{label}</FieldLabel>
                                            <FieldValue>{safeNumber(value)}</FieldValue>
                                          </FieldRow>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    Token metrics per step are not available for this run.
                                  </div>
                                )}
                              </div>
                            )}

                            {pipelineTab === 'logs' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Logs for this run (placeholder).
                                </div>
                                <JsonBlock>
                                  {runData.logs
                                    ? JSON.stringify(runData.logs, null, 2)
                                    : '// Logs are not yet provided by the backend for this run.\n// This block is ready for future log integration.'}
                                </JsonBlock>
                              </div>
                            )}
                          </TabPanel>
                        </CardContent>
                      </Card>
                    </MainColumn>

                  </>
                ) : (
                  <>
                    <MainColumn>
                      <PerformanceGrid>
                        <PerformanceCard>
                      {/* {console.log(pipelineTokenDonutData,"dasdassdsadsad")} */}
                          <GaugesContainer>
                          <GaugeSection>
                            <div style={{ width: 172, height: 172, textAlign: 'center' }}>
                              {pipelineTokenDonutData ? (
                                <>
                                
                                  <Pie data={pipelineTokenDonutData} options={tokenDonutOptions} />
                                  <PerformanceCardLabel>Pipeline Token Usage</PerformanceCardLabel>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      width: '100%',
                                      borderRadius: '999px',
                                      border: '1px dashed #e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      color: 'var(--text-secondary)'
                                    }}
                                  >
                                    No data
                                  </div>
                                  <PerformanceCardLabel>Pipeline Token Usage</PerformanceCardLabel>
                                </>
                              )}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <PerformanceCardValue>{(pipelineInputTokens || 0) + (pipelineOutputTokens || 0) > 0 ? ((pipelineInputTokens || 0) + (pipelineOutputTokens || 0)).toLocaleString() : (totalTokens || 0).toLocaleString()}</PerformanceCardValue>

                              <PerformanceCardStats style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.75rem' }}>
                                <PerformanceCardStat>
                                  <span style={{ color: '#2563eb' }}>●</span>
                                  <span>Input tokens: {(pipelineInputTokens || 0).toLocaleString()}</span>
                                </PerformanceCardStat>
                                <PerformanceCardStat>
                                  <span style={{ color: '#f97316' }}>●</span>
                                  <span>Output tokens: {(pipelineOutputTokens || 0).toLocaleString()}</span>
                                </PerformanceCardStat>
                              </PerformanceCardStats>
                              {/* <ViewDetailsLink>View Details →</ViewDetailsLink> */}
                            </div>
                          </GaugeSection>

                          <GaugeSection>
                            <div style={{ width: 172, height: 172, textAlign: 'center' }}>
                              {pipelineStepTokenPieData ? (
                                <>
                                  <Pie data={pipelineStepTokenPieData} options={tokenDonutOptions} />
                                  <PerformanceCardLabel>Agent-wise Token Usage</PerformanceCardLabel>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      width: '100%',
                                      borderRadius: '999px',
                                      border: '1px dashed #e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      color: 'var(--text-secondary)'
                                    }}
                                  >
                                    No data
                                  </div>
                                  <PerformanceCardLabel>Agent-wise Token Usage</PerformanceCardLabel>
                                </>
                              )}
                            </div>
                            <div style={{textAlign:"right"}}>
                              <PerformanceCardValue>{totalTokens ? totalTokens.toLocaleString() : 'N/A'}</PerformanceCardValue>

                              <PerformanceCardStats style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.75rem' }}>
                                {pipelineStepTokenPieData && pipelineStepTokenPieData.labels.map((label, idx) => (
                                  <PerformanceCardStat key={idx}>
                                    <span style={{ color: pipelineStepTokenPieData.datasets[0].backgroundColor[idx] }}>●</span>
                                    <span>{label}: {pipelineStepTokenPieData.datasets[0].data[idx].toLocaleString()}</span>
                                  </PerformanceCardStat>
                                ))}
                              </PerformanceCardStats>
                              {/* {pipelineStepTokenPieData && pipelineStepTokenPieData.labels.length > 3 && (
                                  <ViewDetailsLink>View Details →</ViewDetailsLink>
                                )} */}
                            </div>
                          </GaugeSection>
                        </GaugesContainer>
                        </PerformanceCard>
                      </PerformanceGrid>

                      <Card>
                        <CardContent>
                          <Tabs>
                            {isToolSelected ? (
                              <>
                                <TabButton
                                  active={activeTab === 'tool-request'}
                                  onClick={() => setActiveTab('tool-request')}
                                >
                                  Request
                                </TabButton>
                                <TabButton
                                  active={activeTab === 'tool-task'}
                                  onClick={() => setActiveTab('tool-task')}
                                >
                                  Task
                                </TabButton>
                                <TabButton
                                  active={activeTab === 'tool-response'}
                                  onClick={() => setActiveTab('tool-response')}
                                >
                                  Response
                                </TabButton>
                                <TabButton
                                  active={activeTab === 'tool-metrics'}
                                  onClick={() => setActiveTab('tool-metrics')}
                                >
                                  Metrics
                                </TabButton>
                              </>
                            ) : (
                              <>
                                <TabButton
                                  active={activeTab === 'request-response'}
                                  onClick={() => setActiveTab('request-response')}
                                >
                                  Request & Response
                                </TabButton>
                                {isNodeContext && (
                                  <TabButton
                                    active={activeTab === 'node-metrics'}
                                    onClick={() => setActiveTab('node-metrics')}
                                  >
                                    Metrics
                                  </TabButton>
                                )}
                                {hasWorkflowVarsTab && (
                                  <TabButton
                                    active={activeTab === 'workflow-vars'}
                                    onClick={() => setActiveTab('workflow-vars')}
                                  >
                                    Workflow Vars
                                  </TabButton>
                                )}
                                {!isNodeSelected && (
                                  <>
                                    <TabButton
                                      active={activeTab === 'conversation'}
                                      onClick={() => setActiveTab('conversation')}
                                    >
                                      Conversation
                                    </TabButton>
                                    <TabButton
                                      active={activeTab === 'system-prompt'}
                                      onClick={() => setActiveTab('system-prompt')}
                                    >
                                      System Prompt
                                    </TabButton>
                                    {hasReasoningTab && (
                                      <TabButton
                                        active={activeTab === 'reasoning'}
                                        onClick={() => setActiveTab('reasoning')}
                                      >
                                        Reasoning
                                      </TabButton>
                                    )}
                                  </>
                                )}
                                {/* Logs tab removed as per request */}
                              </>
                            )}
                          </Tabs>

                          <TabPanel>
                            {isToolSelected && activeTab === 'tool-request' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Tool request (tool_args as JSON)
                                </div>
                                <JsonBlock>{toolArgsJsonText || '// No tool_args available for this tool call.'}</JsonBlock>
                              </div>
                            )}

                            {isToolSelected && activeTab === 'tool-task' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Task</div>
                                {selectedTool?.tool_args?.task ? (
                                  <MarkdownContainer>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {preprocessMarkdownContent(String(selectedTool.tool_args.task))}
                                    </ReactMarkdown>
                                  </MarkdownContainer>
                                ) : (
                                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    (no task present on tool_args)
                                  </div>
                                )}
                              </div>
                            )}

                            {isToolSelected && activeTab === 'tool-response' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Result</div>
                                {toolMainMarkdown ? (
                                  <MarkdownContainer>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {preprocessMarkdownContent(toolMainMarkdown)}
                                    </ReactMarkdown>
                                  </MarkdownContainer>
                                ) : (
                                  <JsonBlock>{'// No result available for this tool call.'}</JsonBlock>
                                )}
                              </div>
                            )}

                            {isToolSelected && activeTab === 'tool-metrics' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Tool metrics</div>
                                <JsonBlock>{selectedTool?.metrics ? JSON.stringify(selectedTool.metrics, null, 2) : '// No metrics available for this tool call.'}</JsonBlock>
                              </div>
                            )}

                            {!isToolSelected && activeTab === 'request-response' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                    Request
                                  </div>
                                  {rrRunData && rrRunData.request != null ? (
                                    typeof rrRunData.request === 'string' ? (
                                      <MarkdownContainer>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                          {preprocessMarkdownContent(rrRunData.request)}
                                        </ReactMarkdown>
                                      </MarkdownContainer>
                                    ) : (
                                      <JsonBlock>{JSON.stringify(rrRunData.request, null, 2)}</JsonBlock>
                                    )
                                  ) : memberInputContent || childRunData.input?.input_content ? (
                                    <MarkdownContainer>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {preprocessMarkdownContent(memberInputContent || childRunData.input?.input_content || '')}
                                      </ReactMarkdown>
                                    </MarkdownContainer>
                                  ) : (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                      (no input content)
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                    Final Response
                                  </div>
                                  {rrRunData && rrRunData.response != null ? (
                                    typeof rrRunData.response === 'string' ? (
                                      <MarkdownContainer>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                          {preprocessMarkdownContent(rrRunData.response)}
                                        </ReactMarkdown>
                                      </MarkdownContainer>
                                    ) : (
                                      <JsonBlock>{JSON.stringify(rrRunData.response, null, 2)}</JsonBlock>
                                    )
                                  ) : (memberMainMarkdown || childMainMarkdown) ? (
                                    <MarkdownContainer>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {preprocessMarkdownContent(memberMainMarkdown || childMainMarkdown)}
                                      </ReactMarkdown>
                                    </MarkdownContainer>
                                  ) : (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                      (no response content)
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {!isToolSelected && hasWorkflowVarsTab && activeTab === 'workflow-vars' && (
                              <div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                                  Workflow variables (session_state.workflow_var) for the selected run.
                                </div>
                                <JsonBlock>
                                  {workflowVars ? JSON.stringify(workflowVars, null, 2) : '// No workflow variables available for this selection.'}
                                </JsonBlock>
                              </div>
                            )}

                            {!isToolSelected && isNodeContext && activeTab === 'node-metrics' && (
                              <div>
                                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', marginBottom: '1rem' }}>
                                  <div style={{ width: 220, height: 220 }}>
                                    {nodeTokenDonutData ? (
                                      <Pie data={nodeTokenDonutData} options={tokenDonutOptions} />
                                    ) : (
                                      <div
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          borderRadius: '999px',
                                          border: '1px dashed #e5e7eb',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.8rem',
                                          color: 'var(--text-secondary)'
                                        }}
                                      >
                                        No token breakdown
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', flex: 1 }}>
                                    <FieldRow>
                                      <FieldLabel>Total tokens</FieldLabel>
                                      <FieldValue>{rightTotalTokens != null ? rightTotalTokens.toLocaleString() : 'N/A'}</FieldValue>
                                    </FieldRow>
                                    <FieldRow>
                                      <FieldLabel>Input tokens</FieldLabel>
                                      <FieldValue>{(rightInputTokens || 0).toLocaleString()}</FieldValue>
                                    </FieldRow>
                                    <FieldRow>
                                      <FieldLabel>Output tokens</FieldLabel>
                                      <FieldValue>{(rightOutputTokens || 0).toLocaleString()}</FieldValue>
                                    </FieldRow>
                                  </div>
                                </div>

                                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
                                  <div style={{ width: 220, height: 220 }}>
                                    {childRunsLoading ? (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: 'var(--text-secondary)' }}>Loading…</div>
                                    ) : nodeAgentPieData ? (
                                      <Pie data={nodeAgentPieData} options={tokenDonutOptions} />
                                    ) : (
                                      <div
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          borderRadius: '999px',
                                          border: '1px dashed #e5e7eb',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.8rem',
                                          color: 'var(--text-secondary)'
                                        }}
                                      >
                                        No agent-wise data
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', flex: 1 }}>
                                    {childRunsLoading ? (
                                      <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
                                    ) : nodeAgentPieData ? (
                                      nodeAgentPieData.labels.map((label, idx) => (
                                        <FieldRow key={label + idx}>
                                          <FieldLabel>
                                            <span style={{ color: nodeAgentPieData.datasets[0].backgroundColor[idx] }}>●</span>
                                            {' '}
                                            {label}
                                          </FieldLabel>
                                          <FieldValue>{nodeAgentPieData.datasets[0].data[idx].toLocaleString()}</FieldValue>
                                        </FieldRow>
                                      ))
                                    ) : (
                                      <div style={{ color: 'var(--text-secondary)' }}>Agent-wise token usage is not available.</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {!isToolSelected && activeTab === 'conversation' && !isNodeSelected && (
                              <div>
                                {(() => {
                                  const activeMessages = memberContext ? memberSortedMessages : childSortedMessages;
                                  if (activeMessages.length === 0) {
                                    return (
                                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                        No conversation messages recorded for this run.
                                      </div>
                                    );
                                  }
                                  return activeMessages.map((m) => (
                                    <TimelineRow key={m.id || `${m.role}-${m.created_at}`}>
                                      <TimelineRail>
                                        <TimelineLine />
                                        <TimelineDot />
                                      </TimelineRail>
                                      <MessageCard>
                                        <MessageHeader>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                            <RoleBadge style={getRoleBadgeStyle(m.role)}>
                                              {String(m.role || 'unknown').toUpperCase()}
                                            </RoleBadge>
                                          </div>
                                          <MessageMeta>{formatTimeOnly(m.created_at)}</MessageMeta>
                                        </MessageHeader>
                                        <MessageContent>
                                          {typeof m.content === 'string' ? (
                                            <MarkdownContainer style={{ maxHeight: 'none', padding: 0 }}>
                                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {preprocessMarkdownContent(m.content)}
                                              </ReactMarkdown>
                                            </MarkdownContainer>
                                          ) : m.content != null ? (
                                            JSON.stringify(m.content, null, 2)
                                          ) : (
                                            ''
                                          )}
                                          {(() => {
                                            const rc = m && m.reasoning_content;
                                            if (typeof rc === 'string' && rc.trim() !== '') {
                                              return (
                                                <div style={{ marginTop: 8 }}>
                                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Reasoning</div>
                                                  <MarkdownContainer style={{ maxHeight: 'none', padding: 0 }}>
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                      {preprocessMarkdownContent(rc)}
                                                    </ReactMarkdown>
                                                  </MarkdownContainer>
                                                </div>
                                              );
                                            } else if (rc != null) {
                                              return (
                                                <div style={{ marginTop: 8 }}>
                                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Reasoning</div>
                                                  <JsonBlock>{JSON.stringify(rc, null, 2)}</JsonBlock>
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </MessageContent>
                                        {m.metrics && (
                                          <div
                                            style={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: '0.25rem',
                                              marginTop: 4,
                                            }}
                                          >
                                            {m.metrics.input_tokens != null && (
                                              <Badge>In: {m.metrics.input_tokens}</Badge>
                                            )}
                                            {m.metrics.output_tokens != null && (
                                              <Badge>Out: {m.metrics.output_tokens}</Badge>
                                            )}
                                            {m.metrics.total_tokens != null && (
                                              <Badge>Total: {m.metrics.total_tokens}</Badge>
                                            )}
                                            {m.metrics.time_to_first_token != null && (
                                              <Badge>TTFT: {m.metrics.time_to_first_token.toFixed(2)}s</Badge>
                                            )}
                                          </div>
                                        )}
                                      </MessageCard>
                                    </TimelineRow>
                                  ));
                                })()}
                              </div>
                            )}

                            {!isToolSelected && activeTab === 'system-prompt' && !isNodeSelected && (
                              <div>
                                <div
                                  style={{
                                    fontSize: '0.8rem',
                                    color: 'var(--text-secondary)',
                                    marginBottom: '0.5rem',
                                  }}
                                >
                                  From metric_run.system_prompt or first system message
                                </div>
                                {childRun?.system_prompt || childMessages.find((m) => m.role === 'system')?.content ? (
                                  <MarkdownContainer>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {preprocessMarkdownContent(childRun?.system_prompt || childMessages.find((m) => m.role === 'system')?.content || '')}
                                    </ReactMarkdown>
                                  </MarkdownContainer>
                                ) : (
                                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    (no system prompt available)
                                  </div>
                                )}
                              </div>
                            )}

                            {!isToolSelected && hasReasoningTab && activeTab === 'reasoning' && !isNodeSelected && (
                              <div>
                                {reasoningSteps.length === 0 ? (
                                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    No reasoning steps available.
                                  </div>
                                ) : (
                                  <>
                                    <ReasoningSummaryRow>
                                      <ReasoningSummaryCard>
                                        <ReasoningSummaryLabel>Steps</ReasoningSummaryLabel>
                                        <ReasoningSummaryValue>{stepsCount}</ReasoningSummaryValue>
                                      </ReasoningSummaryCard>
                                      <ReasoningSummaryCard>
                                        <ReasoningSummaryLabel>Avg confidence</ReasoningSummaryLabel>
                                        <ReasoningSummaryValue>{avgConfidence != null ? `${Math.round(avgConfidence * 100)}%` : '-'}</ReasoningSummaryValue>
                                      </ReasoningSummaryCard>
                                      <ReasoningSummaryCard>
                                        <ReasoningSummaryLabel>Outcome</ReasoningSummaryLabel>
                                        <ReasoningSummaryValue $success={String(reasoningSteps[stepsCount - 1]?.next_action || '').toLowerCase() === 'final_answer'}>
                                          {outcomeLabel || '-'}
                                        </ReasoningSummaryValue>
                                      </ReasoningSummaryCard>
                                    </ReasoningSummaryRow>

                                    <ReasoningControlsRow>
                                      <ReasoningToggleButton onClick={toggleAll}>{allExpanded ? 'Collapse all' : 'Expand all'}</ReasoningToggleButton>
                                    </ReasoningControlsRow>

                                    <ReasoningStepper>
                                      <ReasoningStepperLine />
                                      {reasoningSteps.map((s, idx) => {
                                        const nextAction = String(s?.next_action || '').toLowerCase();
                                        const isFinal = nextAction === 'final_answer';
                                        const rawC = typeof s?.confidence === 'number' ? s.confidence : null;
                                        const clamped = rawC != null ? Math.max(0, Math.min(1, rawC)) : null;
                                        const confPct = clamped != null ? Math.round(clamped * 100) : null;
                                        const low = clamped != null ? clamped < 0.7 : false;
                                        const expanded = expandedSteps.has(idx);

                                        return (
                                          <ReasoningStepRow key={(s && s.title) || idx}>
                                            <ReasoningMarker $final={isFinal}>{isFinal ? '✓' : idx + 1}</ReasoningMarker>
                                            <ReasoningCard
                                              role="button"
                                              tabIndex={0}
                                              onClick={() => toggleStep(idx)}
                                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleStep(idx); } }}
                                            >
                                              <ReasoningHead>
                                                <ReasoningTitle>{s?.title || `Step ${idx + 1}`}</ReasoningTitle>
                                                {nextAction ? (
                                                  <ReasoningBadge $variant={nextAction}>{nextAction === 'final_answer' ? 'final answer' : nextAction}</ReasoningBadge>
                                                ) : null}
                                              </ReasoningHead>
                                              {!expanded && (
                                                <ReasoningHint>Click to view action, result and reasoning</ReasoningHint>
                                              )}
                                              <ReasoningBody $expanded={expanded}>
                                                {s && s.action ? (
                                                  <div style={{ marginTop: 4 }}>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Action</div>
                                                    <MarkdownContainer style={{ maxHeight: 'none', padding: 0 }}>
                                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {String(s.action)}
                                                      </ReactMarkdown>
                                                    </MarkdownContainer>
                                                  </div>
                                                ) : null}
                                                {s && s.result ? (
                                                  <div style={{ marginTop: 6 }}>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Result</div>
                                                    <MarkdownContainer style={{ maxHeight: 'none', padding: 0 }}>
                                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {String(s.result)}
                                                      </ReactMarkdown>
                                                    </MarkdownContainer>
                                                  </div>
                                                ) : null}
                                                {s && s.reasoning ? (
                                                  <div style={{ marginTop: 6 }}>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Reasoning</div>
                                                    <MarkdownContainer style={{ maxHeight: 'none', padding: 0 }}>
                                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {String(s.reasoning)}
                                                      </ReactMarkdown>
                                                    </MarkdownContainer>
                                                  </div>
                                                ) : null}
                                                <ConfidenceRow>
                                                  <ConfidenceLabel>Confidence</ConfidenceLabel>
                                                  <ConfidenceTrack>
                                                    <ConfidenceFill $low={low} $pct={confPct || 0} />
                                                  </ConfidenceTrack>
                                                  <ConfidenceValue>{confPct != null ? `${confPct}%` : '-'}</ConfidenceValue>
                                                </ConfidenceRow>
                                              </ReasoningBody>
                                            </ReasoningCard>
                                          </ReasoningStepRow>
                                        );
                                      })}
                                    </ReasoningStepper>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Logs panel removed as per request */}
                          </TabPanel>
                        </CardContent>
                      </Card>

                    </MainColumn>

                  </>
                )}
              </>
            )}
          </LayoutContainer>
        )}
      </motion.div>
    </PageContainer>
  );
};

export default ObservabilityRunDetails;
